import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { API } from '../../context/AuthContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
  CartesianGrid, Area
} from 'recharts';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Loader2,
  FileText,
  FolderOpen,
  Image,
  History,
  Settings,
  MapPin,
  Map,
  Layers,
  Mail,
  ExternalLink,
  Eye,
  Download,
  Building2,
  CheckCircle2,
  Award,
  UserCheck,
  Users,
  TrendingUp,
  X,
  // Thematic area icons
  Gavel,
  BookOpen,
  Baby,
  HeartPulse,
  Accessibility,
  GraduationCap,
  Leaf,
  Shield,
  Landmark,
  Heart,
  KeyRound,
  Briefcase,
  Brain,
  Activity,
  Apple,
  Droplet,
  Stethoscope
} from 'lucide-react';

// ── AdminNews.js ──────────────────────────────────────────────────────────────
export function AdminNews() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title:'', summary:'', content:'', category:'', is_published:false, is_featured:false });

  const fetch = () => { setLoading(true); API.get('/news?limit=50&admin_view=true').then(r=>setPosts(r.data)).finally(()=>setLoading(false)); };
  useEffect(()=>{ fetch(); },[]);

  const save = async () => {
    try {
      if (editing?.id) { await API.put(`/news/${editing.id}`, form); toast.success('Updated'); }
      else { await API.post('/news', form); toast.success('Created'); }
      setEditing(null); setForm({ title:'', summary:'', content:'', category:'', is_published:false, is_featured:false }); fetch();
    } catch { toast.error('Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await API.delete(`/news/${id}`); toast.success('Deleted'); fetch();
  };

  const startEdit = (post) => { setEditing(post); setForm({ title:post.title, summary:post.summary||'', content:post.content||'', category:post.category||'', is_published:post.is_published, is_featured:post.is_featured }); };

  return (
    <div className="anim-fadeIn">
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={() => { setEditing({}); setForm({ title:'', summary:'', content:'', category:'', is_published:false, is_featured:false }); }} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={16} /> New Post
        </button>
      </div>

      {editing !== null && (
        <div className="card card-pad" style={{ marginBottom:20 }}>
          <h3 style={{ fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <FileText size={18} /> {editing.id ? 'Edit Post' : 'New Post'}
          </h3>
          <div className="form-group"><label className="form-label">Title</label><input className="form-control" value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/></div>
          <div className="form-group"><label className="form-label">Summary</label><textarea className="form-control" value={form.summary} onChange={e=>setForm({...form,summary:e.target.value})}/></div>
          <div className="form-group"><label className="form-label">Content</label><textarea className="form-control" style={{ minHeight:160 }} value={form.content} onChange={e=>setForm({...form,content:e.target.value})}/></div>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                <option value="">Select...</option>
                {['Advocacy','Achievement','Partnership','Operations','Health','Training'].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display:'flex', gap:16, marginBottom:16 }}>
            {[['is_published','Publish'],['is_featured','Featured']].map(([k,l])=>(
              <label key={k} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:'0.85rem', fontWeight:600 }}>
                <input type="checkbox" checked={form[k]} onChange={e=>setForm({...form,[k]:e.target.checked})}/> {l}
              </label>
            ))}
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setEditing(null)} className="btn btn-ghost" style={{ display:'flex', alignItems:'center', gap:4 }}>
              <X size={16} /> Cancel
            </button>
            <button onClick={save} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
              <FileText size={16} /> Save Post
            </button>
          </div>
        </div>
      )}

      {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div> : (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Title</th><th>Category</th><th>Status</th><th>Published</th><th>Actions</th></tr></thead>
            <tbody>
              {posts.map(p=>(
                <tr key={p.id}>
                  <td><div style={{ fontWeight:600, fontSize:'0.85rem' }}>{p.title}</div><div style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>{p.summary?.slice(0,80)}</div></td>
                  <td><span className="badge badge-blue">{p.category||'—'}</span></td>
                  <td><span className={`badge ${p.is_published?'badge-green':'badge-yellow'}`}>{p.is_published?'Published':'Draft'}</span></td>
                  <td style={{ fontSize:'0.78rem', color:'var(--gray-400)' }}>{p.published_at?new Date(p.published_at).toLocaleDateString():'—'}</td>
                  <td><div style={{ display:'flex', gap:6 }}>
                    <button onClick={()=>startEdit(p)} className="btn btn-sm btn-secondary" style={{ display:'flex', alignItems:'center', gap:4 }}><Edit size={14} /> Edit</button>
                    <button onClick={()=>del(p.id)} className="btn btn-sm btn-danger" style={{ display:'flex', alignItems:'center', gap:4 }}><Trash2 size={14} /> Delete</button>
                  </div></td>
                </tr>
              ))}
              {posts.length===0 && <tr><td colSpan={5}><div className="empty-state"><div className="empty-state-icon"><FileText size={48} /></div><div>No posts yet</div></div></td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── AdminResources.js ─────────────────────────────────────────────────────────
export function AdminResources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:'', description:'', resource_type:'', external_url:'', category:'' });

  const fetch = () => { setLoading(true); API.get('/resources?limit=100').then(r=>setItems(r.data)).finally(()=>setLoading(false)); };
  useEffect(()=>{ fetch(); },[]);

  const save = async () => {
    try { await API.post('/resources', form); toast.success('Resource added'); setShowForm(false); fetch(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="anim-fadeIn">
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:16 }}>
        <button onClick={()=>setShowForm(!showForm)} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
          {showForm ? <><X size={16} /> Cancel</> : <><Plus size={16} /> Add Resource</>}
        </button>
      </div>
      {showForm && (
        <div className="card card-pad" style={{ marginBottom:20 }}>
          <h3 style={{ fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
            <FolderOpen size={18} /> Add Resource
          </h3>
          <div className="grid-2">
            <div className="form-group"><label className="form-label">Title</label><input className="form-control" value={form.title} onChange={e=>setForm({...form,title:e.target.value})}/></div>
            <div className="form-group"><label className="form-label">Category</label>
              <select className="form-control" value={form.category} onChange={e=>setForm({...form,category:e.target.value})}>
                <option value="">Select...</option>
                {['Policy','Report','Guideline','Training Material','Research','Template','Other'].map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Resource Type</label><input className="form-control" value={form.resource_type} onChange={e=>setForm({...form,resource_type:e.target.value})} placeholder="PDF, Word, Link..."/></div>
            <div className="form-group"><label className="form-label">External URL</label><input className="form-control" value={form.external_url} onChange={e=>setForm({...form,external_url:e.target.value})} placeholder="https://"/></div>
          </div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-control" value={form.description} onChange={e=>setForm({...form,description:e.target.value})}/></div>
          <div style={{ display:'flex', gap:10 }}>
            <button onClick={()=>setShowForm(false)} className="btn btn-ghost" style={{ display:'flex', alignItems:'center', gap:4 }}><X size={16} /> Cancel</button>
            <button onClick={save} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}><FileText size={16} /> Save</button>
          </div>
        </div>
      )}
      {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div> : (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Title</th><th>Category</th><th>Type</th><th>Downloads</th><th>Actions</th></tr></thead>
            <tbody>
              {items.map(r=>(
                <tr key={r.id}>
                  <td><div style={{ fontWeight:600, fontSize:'0.85rem' }}>{r.title}</div></td>
                  <td><span className="badge badge-blue">{r.category||'—'}</span></td>
                  <td style={{ fontSize:'0.82rem', color:'var(--gray-500)' }}>{r.resource_type||'—'}</td>
                  <td style={{ fontSize:'0.82rem' }}>
                    <span style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <Download size={14} /> {r.download_count||0}
                    </span>
                  </td>
                  <td><button onClick={()=>API.delete(`/resources/${r.id}`).then(()=>{toast.success('Deleted');fetch();})} className="btn btn-sm btn-danger" style={{ display:'flex', alignItems:'center', gap:4 }}><Trash2 size={14} /> Delete</button></td>
                </tr>
              ))}
              {items.length===0 && <tr><td colSpan={5}><div className="empty-state"><div className="empty-state-icon"><FolderOpen size={48} /></div><div>No resources yet</div></div></td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── MediaLibrary.js ───────────────────────────────────────────────────────────
export function MediaLibrary() {
  const [notice] = useState('Media files are managed per-organisation. Go to an Organisation → Edit → Social Media tab to upload gallery photos, or use the upload button on the Organisation Detail page.');
  return (
    <div className="anim-fadeIn">
      <div className="alert alert-info" style={{ marginBottom:20, display:'flex', alignItems:'flex-start', gap:8 }}>
        <Image size={18} style={{ flexShrink:0, marginTop:2 }} /> {notice}
      </div>
      <div className="card card-pad">
        <div className="empty-state">
          <div className="empty-state-icon"><Image size={48} /></div>
          <h3>Media is managed per-organisation</h3>
          <p>Navigate to an organisation's detail or edit page to manage their photos and documents.</p>
        </div>
      </div>
    </div>
  );
}

// ── AuditLogs.js ──────────────────────────────────────────────────────────────
export function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetch = () => {
    setLoading(true);
    API.get(`/audit?page=${page}&limit=30`).then(r=>{ setLogs(r.data.data); setTotal(r.data.total); }).finally(()=>setLoading(false));
  };
  useEffect(()=>{ fetch(); },[page]);

  const ACTION_COLORS = { CREATE_CSO:'badge-green', UPDATE_CSO:'badge-blue', DELETE_CSO:'badge-red', CERTIFY_CSO:'badge-gold', DECERTIFY_CSO:'badge-orange', PUBLISH_CSO:'badge-green', UNPUBLISH_CSO:'badge-yellow', CREATE_USER:'badge-purple', CHANGE_ROLE:'badge-purple', BULK_PUBLISH:'badge-teal' };

  const filtered = logs.filter(l =>
    !search || l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.action?.toLowerCase().includes(search.toLowerCase()) ||
    l.record_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="anim-fadeIn">
      <div style={{ display:'flex', gap:10, marginBottom:16, alignItems:'center' }}>
        <div className="input-group" style={{ maxWidth:300 }}>
          <span className="input-icon"><Search size={16} /></span>
          <input className="form-control" placeholder="Search logs..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <span style={{ fontSize:'0.82rem', color:'var(--gray-500)' }}>
          <History size={14} style={{ marginRight:4, verticalAlign:'middle' }} />
          {total} total entries
        </span>
      </div>
      {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div> : (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Action</th><th>User</th><th>Record</th><th>IP</th><th>Time</th></tr></thead>
            <tbody>
              {filtered.map(l=>(
                <tr key={l.id}>
                  <td><span className={`badge ${ACTION_COLORS[l.action]||'badge-gray'}`} style={{ fontSize:'0.7rem' }}>{l.action?.replace(/_/g,' ')}</span></td>
                  <td style={{ fontSize:'0.82rem', fontWeight:600 }}>{l.user_name||'System'}</td>
                  <td style={{ fontSize:'0.82rem', color:'var(--gray-600)' }}>{l.record_name||l.table_name||'—'}</td>
                  <td style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>{l.ip_address||'—'}</td>
                  <td style={{ fontSize:'0.75rem', color:'var(--gray-400)', whiteSpace:'nowrap' }}>{new Date(l.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ display:'flex', justifyContent:'center', gap:6, marginTop:16 }}>
        {page>1 && <button className="pg-btn" onClick={()=>setPage(p=>p-1)}>‹ Prev</button>}
        <span className="pg-btn active">{page}</span>
        {logs.length===30 && <button className="pg-btn" onClick={()=>setPage(p=>p+1)}>Next ›</button>}
      </div>
    </div>
  );
}

// ── SettingsPage.js ───────────────────────────────────────────────────────────
export function SettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(()=>{ API.get('/settings').then(r=>setSettings(r.data)).finally(()=>setLoading(false)); },[]);

  const save = async () => {
    setSaving(true);
    try { await API.put('/settings', settings); toast.success('Settings saved'); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const fields = [
    { key:'site_name', label:'Site Name' }, { key:'site_tagline', label:'Tagline' },
    { key:'zan_email', label:'Contact Email' }, { key:'zan_phone', label:'Contact Phone' },
    { key:'zan_address', label:'Physical Address' }, { key:'hero_stat_1', label:'Hero Stat 1 (Total Orgs)' },
    { key:'hero_stat_2', label:'Hero Stat 2 (Verified Orgs)' },
  ];

  if (loading) return <div className="loading-center"><Loader2 size={32} className="spin" /></div>;

  return (
    <div className="anim-fadeIn">
      <div className="card card-pad" style={{ maxWidth:680 }}>
        <h3 style={{ fontWeight:700, marginBottom:20, display:'flex', alignItems:'center', gap:8 }}>
          <Settings size={20} /> Site Settings
        </h3>
        {fields.map(f=>(
          <div key={f.key} className="form-group">
            <label className="form-label">{f.label}</label>
            <input className="form-control" value={settings[f.key]||''} onChange={e=>setSettings({...settings,[f.key]:e.target.value})}/>
          </div>
        ))}
        <button onClick={save} disabled={saving} className="btn btn-primary" style={{ display:'flex', alignItems:'center', gap:6 }}>
          {saving ? <><Loader2 size={16} className="spin" /> Saving...</> : <><Settings size={16} /> Save Settings</>}
        </button>
      </div>
    </div>
  );
}

// ── ProvincesAdmin.js ─────────────────────────────────────────────────────────
export function ProvincesAdmin() {
  const [provinces, setProvinces] = useState([]);
  useEffect(()=>{ API.get('/lookup/provinces').then(r=>setProvinces(r.data)); },[]);
  return (
    <div className="anim-fadeIn">
      <h3 style={{ fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
        <MapPin size={20} /> Provinces
      </h3>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>#</th><th>Province</th><th>Code</th></tr></thead>
          <tbody>
            {provinces.map(p=>(
              <tr key={p.id}>
                <td style={{ color:'var(--gray-400)' }}>{p.id}</td>
                <td style={{ fontWeight:600 }}>{p.name}</td>
                <td><span className="badge badge-gray">{p.code||'—'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="alert alert-info" style={{ marginTop:12, display:'flex', alignItems:'center', gap:8 }}>
        <MapPin size={16} /> Province names are pre-seeded from Zimbabwe's 10 provinces. Contact a developer to add or modify provinces.
      </div>
    </div>
  );
}

// ── DistrictsAdmin.js ─────────────────────────────────────────────────────────
export function DistrictsAdmin() {
  const [districts, setDistricts] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [filter, setFilter] = useState('');
  useEffect(()=>{
    API.get('/lookup/districts').then(r=>setDistricts(r.data));
    API.get('/lookup/provinces').then(r=>setProvinces(r.data));
  },[]);
  const filtered = filter ? districts.filter(d=>String(d.province_id)===filter) : districts;
  const provMap = Object.fromEntries(provinces.map(p=>[p.id,p.name]));
  return (
    <div className="anim-fadeIn">
      <h3 style={{ fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
        <Map size={20} /> Districts
      </h3>
      <div style={{ marginBottom:16 }}>
        <select className="form-control" style={{ maxWidth:220 }} value={filter} onChange={e=>setFilter(e.target.value)}>
          <option value="">All Provinces</option>
          {provinces.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>
      <div className="card">
        <table className="data-table">
          <thead><tr><th>#</th><th>District</th><th>Province</th></tr></thead>
          <tbody>
            {filtered.map(d=>(
              <tr key={d.id}>
                <td style={{ color:'var(--gray-400)' }}>{d.id}</td>
                <td style={{ fontWeight:600 }}>{d.name}</td>
                <td><span className="badge badge-green" style={{ fontSize:'0.72rem' }}>{provMap[d.province_id]||'—'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── ThematicAdmin.js ──────────────────────────────────────────────────────────
export function ThematicAdmin() {
  const [items, setItems] = useState([]);
  useEffect(()=>{ API.get('/lookup/thematic').then(r=>setItems(r.data)); },[]);

  // Map thematic area names to specific lucide icons
  const iconMap = {
    'Advocacy & Policy': Gavel,
    'Capacity Building': BookOpen,
    'Child Protection': Baby,
    'Community Health': HeartPulse,
    'Disability & Inclusion': Accessibility,
    'Education': GraduationCap,
    'Environment': Leaf,
    'Gender-Based Violence': Shield,
    'Governance': Landmark,
    'HIV & AIDS': Heart,
    'Key Populations': KeyRound,
    'Livelihoods': Briefcase,
    'Malaria': Stethoscope,
    'Mental Health': Brain,
    'NCDs': Activity,
    'Nutrition': Apple,
    'OVC': Users,
    'Sexual & Reproductive Health': HeartPulse,
    'Tuberculosis (TB)': Stethoscope,
    'WASH': Droplet,
    'Women Empowerment': UserCheck,
    'Youth': Users
  };

  const getIcon = (name) => iconMap[name] || Layers;   // fallback

  return (
    <div className="anim-fadeIn">
      <h3 style={{ fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:8 }}>
        <Layers size={20} /> Thematic Areas
      </h3>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12 }}>
        {items.map(t=>{
          const Icon = getIcon(t.name);
          return (
            <div key={t.id} className="card card-pad" style={{ borderLeft:`4px solid ${t.color||'var(--green)'}` }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ color: 'var(--green)' }}>
                  <Icon size={20} />
                </span>
                <div>
                  <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{t.name}</div>
                  <div style={{ fontSize:'0.72rem', color:'var(--gray-400)' }}>ID: {t.id}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="alert alert-info" style={{ marginTop:16, display:'flex', alignItems:'center', gap:8 }}>
        <Layers size={16} /> Thematic areas are pre-seeded. Contact a developer to add new areas.
      </div>
    </div>
  );
}

// ── ContactInbox.js ───────────────────────────────────────────────────────────
export function ContactInbox() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  useEffect(()=>{ API.get('/contact').then(r=>setMessages(r.data)).finally(()=>setLoading(false)); },[]);

  const markRead = async (id) => {
    await API.patch(`/contact/${id}/read`);
    setMessages(m=>m.map(x=>x.id===id?{...x,is_read:true}:x));
  };

  return (
    <div className="anim-fadeIn">
      <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:16, alignItems:'start' }}>
        <div className="card">
          <div style={{ padding:'14px 18px', borderBottom:'1px solid var(--gray-200)', fontWeight:700, fontSize:'0.9rem', display:'flex', alignItems:'center', gap:8 }}>
            <Mail size={18} /> Inbox ({messages.filter(m=>!m.is_read).length} unread)
          </div>
          {loading ? <div className="loading-center"><Loader2 size={32} className="spin" /></div> : messages.length === 0 ? (
            <div className="empty-state"><div className="empty-state-icon"><Mail size={48} /></div><h3>No messages</h3></div>
          ) : messages.map(m=>(
            <div key={m.id} onClick={() => { setSelected(m); if(!m.is_read) markRead(m.id); }}
              style={{ padding:'14px 18px', borderBottom:'1px solid var(--gray-100)', cursor:'pointer', background:selected?.id===m.id?'var(--green-50)':m.is_read?'white':'#f0f9f0', transition:'all 0.15s' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontWeight:m.is_read?500:700, fontSize:'0.85rem' }}>{m.name}</div>
                  <div style={{ fontSize:'0.78rem', color:'var(--gray-500)', marginTop:2 }}>{m.subject||'(No subject)'}</div>
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                  {!m.is_read && <span className="badge badge-green" style={{ fontSize:'0.65rem' }}>New</span>}
                  <span style={{ fontSize:'0.7rem', color:'var(--gray-400)' }}>{new Date(m.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {selected ? (
          <div className="card card-pad">
            <div style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:'1rem', marginBottom:4 }}>{selected.subject||'(No subject)'}</div>
              <div style={{ fontSize:'0.8rem', color:'var(--gray-500)' }}>From: <strong>{selected.name}</strong> — {selected.email}</div>
              <div style={{ fontSize:'0.75rem', color:'var(--gray-400)', marginTop:2 }}>{new Date(selected.created_at).toLocaleString()}</div>
            </div>
            <div style={{ padding:'14px 16px', background:'var(--gray-50)', borderRadius:8, fontSize:'0.85rem', lineHeight:1.7, color:'var(--gray-700)' }}>
              {selected.message}
            </div>
            <a href={`mailto:${selected.email}?subject=Re: ${selected.subject||''}`}
              className="btn btn-primary" style={{ marginTop:14, display:'inline-flex', alignItems:'center', gap:6 }}>
              <ExternalLink size={16} /> Reply via Email
            </a>
          </div>
        ) : (
          <div className="card card-pad" style={{ textAlign:'center', color:'var(--gray-400)' }}>
            <div style={{ marginBottom:8 }}><Mail size={32} /></div>
            <p style={{ fontSize:'0.85rem' }}>Select a message to read</p>
          </div>
        )}
      </div>
    </div>
  );
}

const CHART_COLORS = [
  '#2e7d32', '#f5a623', '#1565c0', '#c62828', '#6a1b9a', '#00838f', '#e65100', '#4e342e',
  '#558b2f', '#f9a825', '#0d47a1', '#b71c1c', '#4a148c', '#006064', '#bf360c', '#3e2723',
];

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
      padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '0.82rem'
    }}>
      <p style={{ fontWeight: 600, margin: 0, color: 'var(--gray-700)' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '4px 0 0', color: p.color }}>
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export function ReportsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(()=>{ API.get('/stats').then(r=>setStats(r.data)).finally(()=>setLoading(false)); },[]);

  if (loading) return <div className="loading-center"><Loader2 size={32} className="spin" /></div>;

  const clusterData = stats?.by_cluster ? [
    { name:'OVC', value:parseInt(stats.by_cluster.ovc)||0 },
    { name:'KP', value:parseInt(stats.by_cluster.kp)||0 },
    { name:'Health', value:parseInt(stats.by_cluster.health)||0 },
    { name:'Gender', value:parseInt(stats.by_cluster.gender)||0 },
    { name:'Advocacy', value:parseInt(stats.by_cluster.advocacy)||0 },
  ].filter(d => d.value > 0) : [];

  const provinceData = stats?.by_province || [];
  const thematicData = stats?.by_thematic?.slice(0,8) || [];
  const growthData = stats?.monthly_growth || [];

  return (
    <div className="anim-fadeIn" style={{ padding: '0 24px' }}>
      <h3 style={{ fontWeight:700, marginBottom:20, display:'flex', alignItems:'center', gap:8 }}>
        <TrendingUp size={20} /> Reports & Analytics
      </h3>
      
      <div className="grid-4" style={{ marginBottom:20 }}>
        {[
          { label:'Total CSOs', value:stats?.total_csos||0, icon: Building2, color:'#e8f5e8', iconColor:'#2e7d32' },
          { label:'Published', value:stats?.published_csos||0, icon: CheckCircle2, color:'#dbeafe', iconColor:'#1e40af' },
          { label:'ZAN Certified', value:stats?.zan_certified||0, icon: Award, color:'#fef3c7', iconColor:'#92400e' },
          { label:'Women Led', value:stats?.women_led||0, icon: UserCheck, color:'#fce7f3', iconColor:'#9d174d' },
        ].map((s,i) => {
          const StatIcon = s.icon;
          return (
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{ background:s.color, color:s.iconColor }}>
                <StatIcon size={20} />
              </div>
              <div>
                <div className="stat-label">{s.label}</div>
                <div className="stat-value">{s.value.toLocaleString()}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid-2" style={{ marginBottom:20 }}>
        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
            <MapPin size={16} /> CSOs by Province
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={provinceData} layout="vertical" margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="provGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2e7d32" stopOpacity={0.8} />
                  <stop offset="100%" stopColor="#1b5e20" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize:11 }} />
              <YAxis type="category" dataKey="province" width={120} tick={{ fontSize:11 }} />
              <Tooltip content={<CustomTooltip formatter={v => `${v} CSOs`} />} />
              <Bar dataKey="count" fill="url(#provGrad)" radius={[0,4,4,0]} barSize={18} animationDuration={1200} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
            <Layers size={16} /> Program Clusters Distribution
          </h3>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <defs>
                {clusterData.map((_, idx) => (
                  <linearGradient key={`grad${idx}`} id={`pieGrad${idx}`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS[idx % CHART_COLORS.length]} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={CHART_COLORS[(idx+1) % CHART_COLORS.length]} stopOpacity={1} />
                  </linearGradient>
                ))}
              </defs>
              <Pie
                data={clusterData}
                cx="50%" cy="50%"
                innerRadius={55} outerRadius={90}
                paddingAngle={3}
                dataKey="value"
                animationDuration={1200}
                animationBegin={200}
                label={({name, percent}) => `${name} ${(percent * 100).toFixed(0)}%`}
                labelLine={{ stroke: '#aaa', strokeWidth: 1 }}
              >
                {clusterData.map((_, idx) => (
                  <Cell key={idx} fill={`url(#pieGrad${idx})`} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip formatter={v => `${v} CSOs`} />} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:'flex', flexWrap:'wrap', gap:8, justifyContent:'center', marginTop:8 }}>
            {clusterData.map((d, idx) => (
              <span key={idx} style={{ fontSize:'0.7rem', display:'flex', alignItems:'center', gap:4 }}>
                <span style={{ width:10, height:10, borderRadius:'50%', background: CHART_COLORS[idx % CHART_COLORS.length] }} />
                {d.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginBottom:20 }}>
        {/* Thematic Areas */}
        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
            <Layers size={16} /> Top Thematic Areas
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={thematicData} layout="vertical" margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="themGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#f5a623" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#f57c00" stopOpacity={1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tick={{ fontSize:11 }} />
              <YAxis type="category" dataKey="name" width={160} tick={{ fontSize:10 }} />
              <Tooltip content={<CustomTooltip formatter={v => `${v} CSOs`} />} />
              <Bar dataKey="count" fill="url(#themGrad)" radius={[0,4,4,0]} barSize={18} animationDuration={1200} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Monthly Growth Line */}
        <div className="card card-pad" style={{ background: '#fff' }}>
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, marginBottom:16, display:'flex', alignItems:'center', gap:6 }}>
            <TrendingUp size={16} /> Monthly Registration Growth
          </h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={growthData} margin={{ left: 10, right: 10 }}>
              <defs>
                <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize:10 }} tickFormatter={v => new Date(v).toLocaleDateString('en',{ month:'short' })} />
              <YAxis tick={{ fontSize:11 }} />
              <Tooltip content={<CustomTooltip labelFormatter={v => new Date(v).toLocaleDateString('en',{ month:'long', year:'numeric' })} formatter={v => `${v} new`} />} />
              <Area type="monotone" dataKey="count" fill="url(#lineGrad)" stroke="none" />
              <Line type="monotone" dataKey="count" stroke="#2e7d32" strokeWidth={2.5} dot={{ r:4, fill:'#2e7d32', strokeWidth:0 }} activeDot={{ r:6, fill:'#2e7d32' }} animationDuration={1500} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// Default exports for router
export default AdminNews;